<script lang="ts">
  import { npubEncode } from 'nostr-tools/nip19';
  import { getPublicAward, credentialStatus, type BadgeAward, type PublicConfig, type PublicOrganization } from './api';
  import BitcoinWordmark from './BitcoinWordmark.svelte';
  import PeacockBadge from './PeacockBadge.svelte';

  let { eventID, recipient, config }: { eventID: string; recipient: string; config: PublicConfig } = $props();
  let award = $state<BadgeAward | null>(null);
  let issuer = $state<PublicOrganization | null>(null);
  let loading = $state(true);
  let error = $state('');
  let copied = $state('');

  const verificationURL = $derived(`${config.public_url.replace(/\/$/, '')}/credentials/${eventID}/${recipient}`);
  const downloadURL = $derived(`/api/public/awards/${eventID}/open-badges/${recipient}`);
  const credentialID = $derived(`nostr:${eventID}:${recipient}`);
  const linkedInURL = 'https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME';
	const subjectProfileURL = $derived(award?.recipient_profiles?.[recipient] ?? '');
	let now = $state(Date.now());
  $effect(() => { const timer = window.setInterval(() => now = Date.now(), 1000); return () => window.clearInterval(timer); });
  const status = $derived(award ? credentialStatus(award, recipient, now) : 'active');
  const expiresAt = $derived(award?.validity?.[recipient]?.expires_at);
  const acceptance = $derived(award?.acceptances?.[recipient]);

  $effect(() => {
    getPublicAward(eventID).then((value) => {
      if (!value.award.recipients.includes(recipient)) throw new Error('This recipient is not part of the award.');
      award = value.award;
      issuer = value.issuer;
    }).catch((cause) => error = cause instanceof Error ? cause.message : 'Could not load this credential.').finally(() => loading = false);
  });

  async function copy(label: string, value: string) {
    await navigator.clipboard.writeText(value);
    copied = label;
    window.setTimeout(() => { if (copied === label) copied = ''; }, 1800);
  }

  function linkedInFields(): Array<[string, string]> {
    if (!award?.badge) return [];
    return [
      ['Credential name', award.badge.name],
      ['Issuing organization', issuerName()],
      ['Issue month', new Date(award.created_at).toLocaleString(undefined, { month: 'long' })],
      ['Issue year', String(new Date(award.created_at).getFullYear())],
      ...(expiresAt ? [['Expiry date', new Date(expiresAt).toLocaleDateString()] as [string, string]] : []),
      ['Credential ID', credentialID],
      ['Credential URL', verificationURL]
    ];
  }

  async function copyAllLinkedInDetails() {
    const details = linkedInFields().map(([label, value]) => `${label}: ${value}`).join('\n');
    await copy('all LinkedIn details', details);
  }

  function issuerName(): string {
    return issuer?.display_name.trim() || `Nostr organization ${issuer ? npubEncode(issuer.pubkey).slice(0, 12) : award?.issuer_pubkey.slice(0, 12)}…`;
  }

  function hideBrokenArtwork(event: Event) {
    (event.currentTarget as HTMLImageElement).hidden = true;
  }
</script>

<main class="credential-page">
  <a class="brand" href="/" aria-label="Badge Studio by Bitcoin++ home"><BitcoinWordmark /><span>Badge Studio</span></a>
  {#if loading}
    <div class="loading"><PeacockBadge /><p>Verifying this credential…</p></div>
  {:else if !award?.badge}
    <section class="missing"><span>404</span><h1>Credential not found.</h1><p>{error}</p></section>
  {:else}
    <article class="credential">
      <div class="art"><span aria-hidden="true">{award.badge.name.slice(0, 1)}</span><img src={award.badge.image_url} alt={`Artwork for ${award.badge.name}`} onerror={hideBrokenArtwork} /></div>
      <div class="copy">
        <p class="eyebrow">{status !== 'active' ? `${status} organization credential` : acceptance ? 'accepted organization credential' : 'awarded organization credential'}</p>
        <h1>{award.badge.name}</h1>
        <p class="description">{award.badge.description}</p>
        <dl>
			<div><dt>Issued by</dt><dd>{#if issuer?.website_url}<a href={issuer.website_url} target="_blank" rel="noreferrer">{issuerName()} ↗</a>{:else}{issuerName()}{/if}</dd></div>
          <div><dt>Recipient</dt><dd>{npubEncode(recipient)}</dd></div>
          <div><dt>Awarded</dt><dd>{new Date(award.created_at).toLocaleDateString(undefined, { dateStyle: 'long' })}</dd></div>
          {#if expiresAt}<div><dt>Valid until</dt><dd>{new Date(expiresAt).toLocaleString()}</dd></div>{/if}
          <div><dt>Source</dt><dd>Nostr award {eventID.slice(0, 12)}…</dd></div>
          <div><dt>Status</dt><dd>{award.revocation ? `Revoked ${new Date(award.revocation.created_at).toLocaleDateString()}` : status === 'expired' ? 'Expired' : acceptance ? `Accepted ${new Date(acceptance.created_at).toLocaleDateString()}` : 'Awarded · acceptance pending'}</dd></div>
        </dl>
		{#if status === 'expired'}<p class="revoked-notice">This membership has expired. Renewal extends this same credential; its ID and verification link stay the same.</p>{/if}
        {#if award.revocation}<p class="revoked-notice">This credential is no longer valid.{award.revocation.reason ? ` ${award.revocation.reason}` : ''}</p>{/if}
		{#if subjectProfileURL}<a class="subject-profile" href={subjectProfileURL} target="_blank" rel="noreferrer">View recipient profile <span>↗</span></a>{/if}
        {#if status === 'active'}<div class="actions">
          {#if !acceptance}<a class="accept" href={`/claim/${eventID}`}>Accept on Nostr <span>→</span></a>{/if}
          {#if config.open_badges_enabled}<a class="primary" href={downloadURL} download="open-badge-3.jwt">Download Open Badge 3.0</a>{/if}
          <a class="linkedin" href={linkedInURL} target="_blank" rel="noreferrer">Add to LinkedIn <span>↗</span></a>
        </div>{/if}
        {#if status === 'active' && !config.open_badges_enabled}<p class="unavailable">Open Badges downloads are not configured on this server yet.</p>{/if}
        {#if status === 'active'}<section class="linkedin-fields">
          <div class="linkedin-heading">
            <div><h2>LinkedIn details</h2><p>LinkedIn asks you to enter these fields. The verification link points back to this page.</p></div>
            <button class="copy-all" type="button" onclick={copyAllLinkedInDetails}>{copied === 'all LinkedIn details' ? 'Copied all LinkedIn details' : 'Copy all LinkedIn details'}</button>
          </div>
          {#each linkedInFields() as field}
            <button class="field" type="button" onclick={() => copy(field[0], field[1])}><span><small>{field[0]}</small><strong>{field[1]}</strong></span><i>{copied === field[0] ? 'Copied' : 'Copy'}</i></button>
          {/each}
        </section>{/if}
      </div>
    </article>
  {/if}
</main>

<style>
  .credential-page { width: min(1120px, calc(100% - 48px)); min-height: 100dvh; margin: auto; padding: 30px 0 64px; }
  .brand { --wordmark-size: 27px; display: inline-flex; align-items: center; gap: 12px; color: var(--ink); text-decoration: none; }
  .brand > span { padding-left: 12px; border-left: 1px solid var(--line-strong); font-size: 12px; font-weight: 800; }
  .credential { display: grid; grid-template-columns: minmax(320px,.85fr) minmax(430px,1.15fr); margin-top: 28px; border: 1px solid var(--line-strong); background: var(--surface); box-shadow: 18px 21px 0 color-mix(in srgb, var(--aqua) 14%, transparent); }
  .art { display: grid; place-items: center; min-height: 590px; background: linear-gradient(145deg, var(--teal), var(--violet)); }
  .art > * { grid-area: 1/1; width: min(68%,340px); aspect-ratio: 1; border: 10px solid rgba(249,240,220,.92); border-radius: 50%; }
  .art span { display: grid; place-items: center; color: var(--paper); font: 500 100px/1 var(--display); }
  .art img { object-fit: cover; }
  .copy { padding: clamp(34px,5vw,64px); }
  .eyebrow { margin: 0 0 15px; color: var(--violet); font-size: 10px; font-weight: 850; letter-spacing: .15em; }
  h1 { margin: 0; font: 500 clamp(46px,6vw,70px)/.94 var(--display); letter-spacing: -.05em; }
  .description { margin: 20px 0 26px; color: var(--muted); font-size: 16px; line-height: 1.55; }
  dl { margin: 0; border-top: 1px solid var(--line); }
  dl div { display: grid; grid-template-columns: 85px minmax(0,1fr); gap: 12px; padding: 11px 0; border-bottom: 1px solid var(--line); }
  dt { color: var(--muted); font-size: 10px; font-weight: 800; }
  dd { margin: 0; overflow: hidden; font: 11px var(--mono); text-overflow: ellipsis; white-space: nowrap; }
	dd a { color: inherit; text-decoration-color: var(--line-strong); text-underline-offset: 3px; }
  .actions { display: flex; flex-wrap: wrap; gap: 10px; margin: 28px 0 0; }
	.subject-profile { display: inline-flex; gap: 9px; margin-top: 18px; border-bottom: 1px solid currentColor; color: var(--teal); font-size: 12px; font-weight: 850; text-decoration: none; }
  .actions a { display: inline-flex; align-items: center; justify-content: center; min-height: 46px; padding: 0 16px; text-decoration: none; font-size: 12px; font-weight: 850; }
  .primary { background: var(--ink); color: var(--paper); }
  .accept { border: 1px solid var(--teal); color: var(--teal); }
  .linkedin { border: 1px solid #0a66c2; color: #0a66c2; }
  .linkedin span { margin-left: 9px; }
  .unavailable { color: var(--muted); font-size: 12px; }
  .revoked-notice { margin: 20px 0 0; padding: 13px; border-left: 3px solid var(--coral); background: #f9e4e4; color: #842f38; font-size: 13px; line-height: 1.5; }
  .linkedin-fields { margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--line-strong); }
  .linkedin-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; margin-bottom: 14px; }
  .linkedin-fields h2 { margin: 0; font: 500 25px var(--display); }
  .linkedin-heading p { margin: 5px 0 0; color: var(--muted); font-size: 12px; line-height: 1.5; }
  .copy-all { flex: none; min-height: 40px; border: 1px solid #0a66c2; background: transparent; padding: 0 13px; color: #0a66c2; font-size: 11px; font-weight: 850; cursor: pointer; }
  .linkedin-fields .field { display: flex; align-items: center; justify-content: space-between; gap: 18px; width: 100%; min-height: 55px; border: 0; border-top: 1px solid var(--line); background: transparent; padding: 8px 0; color: inherit; text-align: left; cursor: pointer; }
  .linkedin-fields .field span { min-width: 0; }
  .linkedin-fields small { display: block; color: var(--muted); font-size: 9px; font-weight: 800; }
  .linkedin-fields strong { display: block; overflow: hidden; font: 11px var(--mono); text-overflow: ellipsis; white-space: nowrap; }
  .linkedin-fields i { flex: none; color: var(--teal); font-size: 10px; font-style: normal; font-weight: 850; }
  .loading { width: min(340px,80vw); margin: 100px auto; text-align: center; }
  .missing { margin-top: 100px; }
  .missing > span { color: var(--aqua); font: italic 76px var(--display); }
  @media (max-width: 780px) { .credential-page { width: calc(100% - 24px); padding-top: 16px; } .credential { grid-template-columns: 1fr; box-shadow: 9px 12px 0 color-mix(in srgb, var(--aqua) 14%, transparent); } .art { min-height: 330px; } .art > * { width: min(62%,240px); border-width: 7px; } .copy { padding: 36px var(--mobile-gutter) 46px; } .actions { display: grid; } .linkedin-heading { display: grid; } .copy-all { justify-self: start; } }
</style>
